import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getReport, resetReport } from '../utils/perfLog';

// ─────────────────────────────────────────────────────────────────────────────
// TEMPORARY — a measuring instrument, not a feature. Reachable only by typing
// /velocita: nothing in the app links to it. Delete before launch together with
// utils/perfLog.js (that file lists everything to remove).
//
// It answers one question we have so far only guessed at: where does the time
// actually go? Sorted by TOTAL time waited, not by the slowest single call — a
// request that takes 300 ms but happens forty times costs more of your life
// than one that takes two seconds once.
// ─────────────────────────────────────────────────────────────────────────────

function ms(n) {
  if (n == null) return '—';
  return n >= 1000 ? `${(n / 1000).toFixed(1)} s` : `${n} ms`;
}

export default function SpeedReport() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(() => getReport());
  const [copied, setCopied] = useState(false);

  const totalWaited = rows.reduce((sum, r) => sum + r.totalMs, 0);
  const totalCalls  = rows.reduce((sum, r) => sum + r.calls, 0);
  const totalCached = rows.reduce((sum, r) => sum + r.cached, 0);

  // Plain text, so it can be pasted straight into a message.
  function asText() {
    const lines = [
      `MeMo — misurazione velocita`,
      `Attesa totale: ${Math.round(totalWaited / 1000)} s su ${totalCalls} richieste ` +
        `(${totalCached} servite dalla memoria, senza chiedere al server)`,
      '',
      'richiesta | volte | in memoria | media | di cui server | peggiore | totale',
    ];
    rows.forEach(r => {
      lines.push(
        `${r.key} | ${r.calls} | ${r.cached} | ${r.avgMs} ms | ` +
        `${r.avgServerMs == null ? '-' : r.avgServerMs + ' ms'} | ${r.worstMs} ms | ${r.totalMs} ms` +
        (r.failed ? ` | ${r.failed} fallite` : '')
      );
    });
    return lines.join('\n');
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(asText());
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (_) {
      setCopied(false);
    }
  }

  return (
    <div style={{ padding: '8px 0 32px' }}>
      {/* Every screen in MeMo has a way out. This one is reached by typing an
          address, so it is the one place you could land with nothing pointing
          back — the bar at the bottom is there, but 'back' is what a person
          looks for. Same green circle as the event form. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <button type="button" onClick={() => navigate(-1)} aria-label="Indietro" title="Indietro"
          style={{ width: 38, height: 38, flexShrink: 0, padding: 0, display: 'flex', alignItems: 'center',
                   justifyContent: 'center', borderRadius: '50%', cursor: 'pointer',
                   background: 'var(--color-primary)', border: 'none', color: '#fff' }}>
          <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2"
               strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
        </button>
        <h2 className="page-title" style={{ margin: 0 }}>Velocità — misurazione</h2>
      </div>

      <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', lineHeight: 1.5, marginBottom: 16 }}>
        Usa l'app normalmente per un giorno, poi torna qui e premi <b>Copia</b>:
        incolla il risultato in chat. Qui non finisce nulla di quello che scrivi
        negli eventi — solo quanto ci mette ogni tipo di richiesta. Resta su
        questo telefono.
      </p>

      <div style={{
        display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18,
        fontSize: '0.85rem', color: 'var(--color-text)',
      }}>
        <span><b>{Math.round(totalWaited / 1000)} s</b> di attesa in tutto</span>
        <span style={{ color: 'var(--color-text-muted)' }}>·</span>
        <span><b>{totalCalls}</b> richieste</span>
        <span style={{ color: 'var(--color-text-muted)' }}>·</span>
        <span><b>{totalCached}</b> risparmiate dalla memoria</span>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
        <button type="button" className="btn btn-outline btn-sm" onClick={() => setRows(getReport())}>
          Aggiorna
        </button>
        <button type="button" className="btn btn-outline btn-sm" onClick={copy}>
          {copied ? 'Copiato ✓' : 'Copia'}
        </button>
        <button
          type="button"
          className="btn btn-outline btn-sm"
          style={{ borderColor: 'var(--color-stripe-red)', color: 'var(--color-stripe-red)' }}
          onClick={() => { resetReport(); setRows([]); }}
        >
          Azzera
        </button>
      </div>

      {rows.length === 0 && (
        <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
          Ancora niente da mostrare. Gira per l'app e torna qui.
        </p>
      )}

      {/* Wide table on a narrow phone: let it scroll on its own rather than
          squeezing the numbers into columns too thin to read. */}
      {rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: '0.78rem', width: '100%', minWidth: 560 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--color-text-muted)' }}>
                <th style={{ padding: '6px 8px' }}>richiesta</th>
                <th style={{ padding: '6px 8px' }}>volte</th>
                <th style={{ padding: '6px 8px' }}>in memoria</th>
                <th style={{ padding: '6px 8px' }}>media</th>
                <th style={{ padding: '6px 8px' }}>di cui server</th>
                <th style={{ padding: '6px 8px' }}>peggiore</th>
                <th style={{ padding: '6px 8px' }}>totale</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.key} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', fontWeight: 600 }}>
                    {r.key}
                    {r.failed > 0 && (
                      <span style={{ color: '#c0392b', fontWeight: 700 }}> · {r.failed} fallite</span>
                    )}
                  </td>
                  <td style={{ padding: '6px 8px' }}>{r.calls}</td>
                  <td style={{ padding: '6px 8px', color: 'var(--color-primary)' }}>{r.cached}</td>
                  <td style={{ padding: '6px 8px' }}>{ms(r.avgMs)}</td>
                  <td style={{ padding: '6px 8px', color: 'var(--color-text-muted)' }}>{ms(r.avgServerMs)}</td>
                  <td style={{ padding: '6px 8px' }}>{ms(r.worstMs)}</td>
                  <td style={{ padding: '6px 8px', fontWeight: 700 }}>{ms(r.totalMs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: 18, lineHeight: 1.5 }}>
        <b>media</b> = quanto aspetti in genere · <b>di cui server</b> = quanto
        di quell'attesa è il server che lavora (il resto è la connessione) ·
        <b> totale</b> = tempo speso ad aspettare quella richiesta in tutto. La
        riga in cima è quella che costa di più.
      </p>
    </div>
  );
}
